import { useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Modal } from '@shared/ui/Modal';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { AGENT_LABELS, ANNOUNCEABLE_AGENT_CODES } from '@shared/agents/labels';
import {
  useListAdminAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  usePublishAnnouncementMutation,
  useDeleteAnnouncementMutation,
} from '@shared/api';
import {
  announcementAgentLabel,
  kindMeta,
  type AdminAnnouncement,
  type AnnouncementKind,
} from '@entities/announcement';
import { PrCandidatesPanel } from '@features/announcements/ui/PrCandidatesPanel';
import { PublishedPrsPanel } from '@features/announcements/ui/PublishedPrsPanel';

/* ── options ─────────────────────────────────────────────────────────────── */

const KIND_OPTIONS: AnnouncementKind[] = ['new', 'improvement', 'fix'];

// Границы длины — зеркалят backend-валидатор (routes/announcements.rs).
const TITLE_MAX = 200;
const BODY_MAX = 5000;

/**
 * «Система» (пустое значение → agent_code=null) + только те агенты, которые
 * backend принимает (ANNOUNCEABLE_AGENT_CODES). postavka/kadry из AGENT_LABELS
 * сюда НЕ попадают — иначе выбор такого кода дал бы 400 на create/update.
 */
const AGENT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Система (общее)' },
  ...ANNOUNCEABLE_AGENT_CODES.map((value) => ({ value, label: AGENT_LABELS[value] ?? value })),
];

function formatDt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ── layout ──────────────────────────────────────────────────────────────── */

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  flex-wrap: wrap;
`;

const Lead = styled.p`
  margin: 0;
  max-width: 640px;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SectionHeading = styled.h2`
  margin: ${({ theme }) => theme.spacing(6)} 0 ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const AnnCard = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const KindBadge = styled(Badge)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const AnnTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(1.5)};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
`;

const AnnBody = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Meta = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(4)};
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};

  & > div {
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
  }
  & dt {
    font-weight: 600;
  }
  & dd {
    margin: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: stretch;

  @media (max-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing(8)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  &:hover:not(:focus) {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  justify-content: flex-end;
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.status.danger}15;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

/* ── card ────────────────────────────────────────────────────────────────── */

function AnnouncementRow({
  ann,
  onEdit,
  onPublish,
  onDelete,
  publishing,
}: {
  ann: AdminAnnouncement;
  onEdit: (a: AdminAnnouncement) => void;
  onPublish: (id: string) => void;
  onDelete: (a: AdminAnnouncement) => void;
  publishing: boolean;
}) {
  const meta = kindMeta(ann.kind);
  const { Icon } = meta;
  const isDraft = ann.status === 'draft';
  return (
    <AnnCard>
      <div>
        <CardHead>
          <KindBadge $tone={meta.tone}>
            <Icon size={12} />
            {meta.label}
          </KindBadge>
          <Badge $tone="neutral">{announcementAgentLabel(ann.agent_code)}</Badge>
          <Badge $tone={isDraft ? 'warning' : 'success'}>
            {isDraft ? 'Черновик' : 'Опубликовано'}
          </Badge>
        </CardHead>
        <AnnTitle>{ann.title}</AnnTitle>
        {ann.body && <AnnBody>{ann.body}</AnnBody>}
        <Meta>
          <div>
            <dt>Создано:</dt>
            <dd>{formatDt(ann.created_at)}</dd>
          </div>
          {ann.published_at && (
            <div>
              <dt>Опубликовано:</dt>
              <dd>{formatDt(ann.published_at)}</dd>
            </div>
          )}
          {ann.expires_at && (
            <div>
              <dt>Истекает:</dt>
              <dd>{formatDt(ann.expires_at)}</dd>
            </div>
          )}
          {ann.source_ref && (
            <div>
              <dt>Источник:</dt>
              <dd>{ann.source_ref}</dd>
            </div>
          )}
        </Meta>
      </div>
      <Actions>
        {isDraft && (
          <>
            <Button
              $variant="primary"
              $size="sm"
              disabled={publishing}
              onClick={() => onPublish(ann.id)}
            >
              {publishing ? 'Публикация…' : 'Опубликовать'}
            </Button>
            <Button $variant="secondary" $size="sm" onClick={() => onEdit(ann)}>
              Изменить
            </Button>
          </>
        )}
        <Button $variant="ghost" $size="sm" onClick={() => onDelete(ann)}>
          Удалить
        </Button>
      </Actions>
    </AnnCard>
  );
}

/* ── tab ─────────────────────────────────────────────────────────────────── */

export function AdminAnnouncementsTab() {
  const { data: items = [], isFetching } = useListAdminAnnouncementsQuery();
  const [publishAnnouncement, { isLoading: isPublishing }] = usePublishAnnouncementMutation();
  const [deleteAnnouncement, { isLoading: isDeleting }] = useDeleteAnnouncementMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminAnnouncement | null>(null);
  const [deleting, setDeleting] = useState<AdminAnnouncement | null>(null);

  const { drafts, published } = useMemo(() => {
    const d: AdminAnnouncement[] = [];
    const p: AdminAnnouncement[] = [];
    for (const a of items) (a.status === 'draft' ? d : p).push(a);
    const byCreated = (a: AdminAnnouncement, b: AdminAnnouncement) =>
      (b.created_at ?? '').localeCompare(a.created_at ?? '');
    d.sort(byCreated);
    p.sort((a, b) => (b.published_at ?? '').localeCompare(a.published_at ?? '') || byCreated(a, b));
    return { drafts: d, published: p };
  }, [items]);

  return (
    <>
      <Toolbar>
        <Lead>
          Ревью карточек «Что нового» перед рассылкой. Черновики создаются вручную или агентом из
          смердженных изменений — проверьте формулировку и опубликуйте, чтобы разослать
          пользователям.
        </Lead>
        <Button $variant="primary" onClick={() => setCreateOpen(true)}>
          + Создать черновик
        </Button>
      </Toolbar>

      <PrCandidatesPanel />
      <PublishedPrsPanel />

      {isFetching && items.length === 0 ? (
        <EmptyState>Загрузка…</EmptyState>
      ) : items.length === 0 ? (
        <EmptyState>Пока нет карточек обновлений.</EmptyState>
      ) : (
        <>
          <SectionHeading>Черновики ({drafts.length})</SectionHeading>
          {drafts.length === 0 ? (
            <EmptyState>Нет черновиков на ревью.</EmptyState>
          ) : (
            <Stack>
              {drafts.map((ann) => (
                <AnnouncementRow
                  key={ann.id}
                  ann={ann}
                  onEdit={setEditing}
                  onPublish={(id) => void publishAnnouncement(id)}
                  onDelete={setDeleting}
                  publishing={isPublishing}
                />
              ))}
            </Stack>
          )}

          <SectionHeading>Опубликованные ({published.length})</SectionHeading>
          {published.length === 0 ? (
            <EmptyState>Ещё ничего не опубликовано.</EmptyState>
          ) : (
            <Stack>
              {published.map((ann) => (
                <AnnouncementRow
                  key={ann.id}
                  ann={ann}
                  onEdit={setEditing}
                  onPublish={(id) => void publishAnnouncement(id)}
                  onDelete={setDeleting}
                  publishing={isPublishing}
                />
              ))}
            </Stack>
          )}
        </>
      )}

      <CreateAnnouncementModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {editing && (
        <EditAnnouncementModal ann={editing} onClose={() => setEditing(null)} />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Удалить карточку обновления"
        warning={
          deleting
            ? `Карточка «${deleting.title}» будет удалена без возможности восстановления.`
            : ''
        }
        confirmText="удалить"
        confirmLabel="Введите «удалить» для подтверждения"
        confirmButtonLabel="Удалить"
        busyLabel="Удаление…"
        isBusy={isDeleting}
        onConfirm={async () => {
          if (!deleting) return;
          await deleteAnnouncement(deleting.id)
            .unwrap()
            .catch(() => {});
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

/* ── create / edit modals ────────────────────────────────────────────────── */

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { data?: { error?: { message?: string } } })?.data?.error?.message ?? fallback
  );
}

function CreateAnnouncementModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [agentCode, setAgentCode] = useState('');
  const [kind, setKind] = useState<AnnouncementKind>('new');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createAnnouncement, { isLoading }] = useCreateAnnouncementMutation();

  function reset() {
    setAgentCode('');
    setKind('new');
    setTitle('');
    setBody('');
    setSourceRef('');
    setError(null);
  }
  function close() {
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await createAnnouncement({
        agent_code: agentCode || null,
        kind,
        title: title.trim(),
        body: body.trim(),
        source_ref: sourceRef.trim() || null,
      }).unwrap();
      close();
    } catch (err) {
      setError(apiError(err, 'Не удалось создать карточку'));
    }
  }

  return (
    <Modal open={open} title="Новая карточка обновления" onClose={close}>
      <form onSubmit={onSubmit}>
        {error && <ErrorBox>{error}</ErrorBox>}
        <AnnouncementFields
          agentCode={agentCode}
          kind={kind}
          title={title}
          body={body}
          onAgentCode={setAgentCode}
          onKind={setKind}
          onTitle={setTitle}
          onBody={setBody}
        />
        <Field>
          <Label htmlFor="ann-source">Источник (необязательно)</Label>
          <Input
            id="ann-source"
            value={sourceRef}
            onChange={(e) => setSourceRef(e.target.value)}
            placeholder="напр. PR #613 — ключ идемпотентности"
          />
        </Field>
        <ModalActions>
          <Button type="button" $variant="ghost" onClick={close}>
            Отмена
          </Button>
          <Button type="submit" $variant="primary" disabled={isLoading || !title.trim()}>
            {isLoading ? 'Создание…' : 'Создать черновик'}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}

function EditAnnouncementModal({
  ann,
  onClose,
}: {
  ann: AdminAnnouncement;
  onClose: () => void;
}) {
  const [agentCode, setAgentCode] = useState(ann.agent_code ?? '');
  const [kind, setKind] = useState<AnnouncementKind>(ann.kind);
  const [title, setTitle] = useState(ann.title);
  const [body, setBody] = useState(ann.body);
  const [error, setError] = useState<string | null>(null);
  const [updateAnnouncement, { isLoading }] = useUpdateAnnouncementMutation();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await updateAnnouncement({
        id: ann.id,
        patch: {
          agent_code: agentCode || null,
          kind,
          title: title.trim(),
          body: body.trim(),
        },
      }).unwrap();
      onClose();
    } catch (err) {
      setError(apiError(err, 'Не удалось сохранить'));
    }
  }

  return (
    <Modal open title="Изменить карточку" onClose={onClose}>
      <form onSubmit={onSubmit}>
        {error && <ErrorBox>{error}</ErrorBox>}
        <AnnouncementFields
          agentCode={agentCode}
          kind={kind}
          title={title}
          body={body}
          onAgentCode={setAgentCode}
          onKind={setKind}
          onTitle={setTitle}
          onBody={setBody}
        />
        <ModalActions>
          <Button type="button" $variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" $variant="primary" disabled={isLoading || !title.trim()}>
            {isLoading ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}

/** Общие поля формы карточки (агент, тип, заголовок, текст). */
function AnnouncementFields({
  agentCode,
  kind,
  title,
  body,
  onAgentCode,
  onKind,
  onTitle,
  onBody,
}: {
  agentCode: string;
  kind: AnnouncementKind;
  title: string;
  body: string;
  onAgentCode: (v: string) => void;
  onKind: (v: AnnouncementKind) => void;
  onTitle: (v: string) => void;
  onBody: (v: string) => void;
}) {
  return (
    <>
      <Field>
        <Label htmlFor="ann-agent">Агент</Label>
        <Select id="ann-agent" value={agentCode} onChange={(e) => onAgentCode(e.target.value)}>
          {AGENT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field>
        <Label htmlFor="ann-kind">Тип обновления</Label>
        <Select
          id="ann-kind"
          value={kind}
          // Значение select ограничено KIND_OPTIONS — тремя валидными
          // AnnouncementKind, поэтому сужение строки безопасно.
          onChange={(e) => onKind(e.target.value as AnnouncementKind)}
        >
          {KIND_OPTIONS.map((k) => (
            <option key={k} value={k}>
              {kindMeta(k).label}
            </option>
          ))}
        </Select>
      </Field>
      <Field>
        <Label htmlFor="ann-title">Заголовок</Label>
        <Input
          id="ann-title"
          value={title}
          onChange={(e) => onTitle(e.target.value)}
          placeholder="Коротко и понятно, без ИТ-терминов"
          maxLength={TITLE_MAX}
          required
        />
      </Field>
      <Field>
        <Label htmlFor="ann-body">Текст</Label>
        <Textarea
          id="ann-body"
          value={body}
          onChange={(e) => onBody(e.target.value)}
          placeholder="Что изменилось и чем это полезно пользователю"
          maxLength={BODY_MAX}
        />
      </Field>
    </>
  );
}
